
import React, { Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import SubHeader from '@components/ssap/headers/SubHeader';
import SummaryItem from '@components/ssap/pages/pageIncludes/summary/SummaryItem';

// Contexts

import { Context } from '@contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '@hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { getFrequencyId } from '@utils/householdMember/getFrequencyId';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import { isEqual, pick } from '@utils/miscellaneous/objectHelpers';



const EmployerCoverageDetails = props => {
    const {
        applicantGuid,
        employerSponsoredCoverage,
        householdMembers,
        previousData,
        isFinalSummaryPage,
        isSsapInReviewMode
    } = props;

    const intlApi = useIntl();

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const previousEmployerSponsoredCoverage = useMemo(
        () => {
            const index = previousData.taxHousehold.householdMember.findIndex(hhm => hhm.applicantGuid === applicantGuid);
            if(~index) {
                const {
                    employerSponsoredCoverage
                } = previousData.taxHousehold.householdMember[index];
                return employerSponsoredCoverage;
            }
            return [];
        },
        [ previousData ]
    );

    const [
        arePreviousEmployerCoverageDetailsShown,
        toggleEmployerCoverageDetailsVisibility
    ] = useState(!previousEmployerSponsoredCoverage.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        toggleEmployerCoverageDetailsVisibility(isShown => !isShown);
    };

    const getCoverages = (coverages = [], arePrevious = false) => {
        if (coverages.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return coverages.map((coverage, index) => {
            const {
                applicantGuid,
                companyName,
                employerPremium,
                employerPremiumFrequency,
                minimumValuePlan,
                phone
            } = coverage;

            const resolvedHouseholdMembers = arePrevious
                ? previousData.taxHousehold.householdMember :
                householdMembers;

            const employeeName = householdMemberTools
                .extractFromHouseholdMembers(resolvedHouseholdMembers)
                .getFullNameByHhmGuid(applicantGuid);

            const getCombinedValue = (employerPremium, employerPremiumFrequency) => {
                const formattedEmployerPremium = currencyTools.getMoneyFormat(employerPremium, true);
                const formattedFrequency = intlApi.formatMessage({
                    id: getFrequencyId(employerPremiumFrequency, true as any),
                });
                return `${ formattedEmployerPremium } / ${ formattedFrequency }`;
            };

            return (
                <div
                    className = 'grid-col-12'
                    key = { index }
                >
                    <div className = 'review-document grid-row' >
                        <div className = 'grid-col-12 margin-y-0' >
                            <div className = 'grid-row margin-bottom-3' >
                                <SummaryItem
                                    textCode = 'common.employer_name'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { companyName }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.employee_name'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { employeeName }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.contact_number'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { phoneNumberTools.getAdjustedPhoneNumber(phone) }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                {
                                    (minimumValuePlan && employerPremium > 0 && employerPremiumFrequency !== 'UNSPECIFIED')
                                        ? (
                                            <Fragment>
                                                <SummaryItem
                                                    textCode = 'ssap.additional_info.summary.plan_meets_standard'
                                                    isDiffSwitchingIgnored
                                                    value = { minimumValuePlan }
                                                    isStrikeThroughValue = { arePrevious }
                                                />
                                                <SummaryItem
                                                    textCode = 'ssap.additional_info.summary.aptc_for_lower_cost'
                                                    ignoreIntl
                                                    isDiffSwitchingIgnored
                                                    value = { getCombinedValue(employerPremium, employerPremiumFrequency) }
                                                    isStrikeThroughValue = { arePrevious }
                                                />
                                            </Fragment>
                                        )
                                        : null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const filterEmployerSponsoredCoverageProps = (coverages = []) => {
        return coverages.map(coverage => {
            return pick(
                coverage,
                [
                    'companyName',
                    'phone',
                    'minimumValuePlan',
                    'employerPremium',
                    'employerPremiumFrequency'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(
                    filterEmployerSponsoredCoverageProps(employerSponsoredCoverage),
                    filterEmployerSponsoredCoverageProps(previousEmployerSponsoredCoverage)
                )
            };
        },
        [ employerSponsoredCoverage, previousEmployerSponsoredCoverage ]
    );


    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && previousEmployerSponsoredCoverage.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    useSummaryItemDiffRegister({
        diffs: { status: employerSponsoredCoverage.length === 0 ? false : diffs.status },
        summaryContext,
        values: {
            category: 'employer coverage',
            value: employerSponsoredCoverage,
            previousValue: previousEmployerSponsoredCoverage,
            textCode: applicantGuid
        }
    });

    if(employerSponsoredCoverage.length === 0) {
        return null;
    }

    // We highlight background only on Final Review page (SSAP in edit mode) if we are tracking a diff
    const displayUpdatedRow = (diffs.status && isFinalSummaryPage && !isSsapInReviewMode)
        ? 'bg-updated_fields'
        : '';

    return (
        <div className = 'grid-col-12' >
            <div className = 'summary__header'>
                <SubHeader
                    { ...{
                        headerClassName: 'gi-heading-summary margin-bottom-1',
                        dataTestId: `Coverage-Info-Header-${applicantGuid}`,
                        text: 'ssap.subheader.employer_coverage_details',
                        ...linkProps
                    } }
                />
            </div>
            <div className = { displayUpdatedRow } >
                {
                    arePreviousEmployerCoverageDetailsShown && getCoverages(previousEmployerSponsoredCoverage, true)
                }
                {
                    getCoverages(employerSponsoredCoverage)
                }
            </div>
        </div>
    );
};

export default EmployerCoverageDetails;